import { ReactNode, useEffect, useCallback, useState, useMemo, PropsWithChildren } from 'react';
import { FormProvider, useForm, useFormContext, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, Divider, Grid2, Paper, Stack, Typography, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';

import { FormField, FormFieldType } from '@enroller/admissions-components';
import { useHeaderContext, ValidationType } from '../../../contexts/HeaderContext.tsx';
import { usePageTitle } from '@enroller/utils';
import { yesNoOptions } from '../../../constants/yesNoOptions.ts';
import {
  defaultValues,
  yourExperienceSchema,
  YourExperienceFormValues,
  jobExperienceDefaultValues,
  uniExperienceDefaultValues,
  certificateDefaultValues,
} from './YourExperienceFormValues.ts';

// Maximum allowed characters for description fields.
const MAX_CHARACTERS_DESCRIPTION = 1000;

const locationCountryOptions = [
  { label: 'United States', value: 'United States' },
  { label: 'Canada', value: 'Canada' },
  { label: 'Mexico', value: 'Mexico' },
  { label: 'United Kingdom', value: 'UnitedKingdom' },
  { label: 'Germany', value: 'Germany' },
  { label: 'France', value: 'France' },
  { label: 'Italy', value: 'Italy' },
  { label: 'Spain', value: 'Spain' },
  { label: 'China', value: 'China' },
  { label: 'Japan', value: 'Japan' },
  { label: 'South Korea', value: 'SouthKorea' },
  { label: 'India', value: 'India' },
  { label: 'Australia', value: 'Australia' },
  { label: 'Brazil', value: 'Brazil' },
  { label: 'South Africa', value: 'SouthAfrica' },
  { label: 'Other', value: 'Other' },
];

const employmentStatusOptions = [{ value: 'FullTime' }, { value: 'PartTime' }];

const statusOfDegreeOptions = [
  { label: 'Earned', value: 'Earned' },
  { label: 'In Progress', value: 'InProgress' },
  { label: 'Complete', value: 'Complete' },
  { label: 'On hold', value: 'OnHold' },
];

function shouldDisableField(value: string, condition: 'yes' | 'no'): boolean {
  return value !== condition;
}

export const YourExperienceForm = () => {
  return (
    <PageContainer>
      <PaperSection title="Resume" pageTitle="Your Experience">
        <ResumeSection />
      </PaperSection>
      <PaperSection title="General Information">
        <GeneralInformationSection />
      </PaperSection>
      <PaperSection title="Mandatory Questions">
        <MandatoryQuestionsSection />
      </PaperSection>
    </PageContainer>
  );
};

const ResumeSection = () => {
  const theme = useTheme();
  const { control, watch, setValue } = useFormContext<YourExperienceFormValues>();
  const watchResumeType = watch('resume.resumeType');

  const setResumeType = (newValue: 'document' | 'builder' | 'linkedin') => {
    setValue('resume.resumeType', newValue);
  };

  return (
    <Grid2 container size={12} spacing={3} mb={3}>
      <Grid2 size={12}>
        <Alert variant="outlined" severity="info">
          <Typography variant="body2">Please select one of the following options for your resume</Typography>
        </Alert>
      </Grid2>

      <Grid2 size={12}>
        <Stack
          p={1}
          width={1}
          spacing={1}
          direction="row"
          sx={{
            borderRadius: theme.spacing(1.5),
            backgroundColor: theme.palette.grey[50],
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            color="secondary"
            sx={watchResumeType === 'document' ? theme.mixins.activeButton : {}}
            onClick={() => setResumeType('document')}
          >
            Upload your Resume
          </Button>
          <Button
            fullWidth
            variant="outlined"
            color="secondary"
            sx={watchResumeType === 'builder' ? theme.mixins.activeButton : {}}
            onClick={() => setResumeType('builder')}
          >
            Resume Builder
          </Button>
          <Button
            fullWidth
            variant="outlined"
            color="secondary"
            sx={watchResumeType === 'linkedin' ? theme.mixins.activeButton : {}}
            onClick={() => setResumeType('linkedin')}
          >
            LinkedIn Profile
          </Button>
        </Stack>
      </Grid2>

      {watchResumeType === 'document' && (
        <Grid2 size={12}>
          <FormField label="" id="resumeFile" control={control} name="resume.document" type={FormFieldType.FileUploader} />
        </Grid2>
      )}

      {watchResumeType === 'builder' && (
        <>
          <Grid2 size={12}>
            <Typography variant="subtitle1">Resume Builder</Typography>
          </Grid2>
          <Grid2 size={12}>
            <Paper sx={{ p: 2, width: '100%' }}>
              <ProfessionalExperienceSection />
              <UniversityCollegeExperienceSection />
              <ProfessionalCertificatesSection />
            </Paper>
          </Grid2>
        </>
      )}

      {watchResumeType === 'linkedin' && <LinkedInProfileSection />}
    </Grid2>
  );
};

const ProfessionalExperienceSection = () => {
  const { control } = useFormContext<YourExperienceFormValues>();
  const { fields: jobExperiences, append: addExperience, remove } = useFieldArray({ control, name: 'resume.jobExperiences' });

  const handleAddJobExperienceBlock = useCallback(() => {
    addExperience(jobExperienceDefaultValues);
  }, [addExperience]);

  const handleDeleteJobExperienceBlock = useCallback(
    (i: number) => {
      remove(i);
    },
    [remove],
  );

  return (
    <Grid2 container spacing={3}>
      <Grid2 size={12}>
        <Typography variant="subtitle1">Professional Experience</Typography>
      </Grid2>

      <Grid2 size={12}>
        <Alert variant="outlined" severity="info">
          <Typography variant="body2">Please add your professional experience details.</Typography>
        </Alert>
      </Grid2>

      {jobExperiences.map((field, index) => (
        <Grid2 container key={field.id}>
          {index > 0 && <Divider variant="fullWidth" sx={{ width: '100%', mb: 4 }} />}
          <Grid2 size={4}>
            <FormField control={control} label="Business Name" type={FormFieldType.Text} name={`resume.jobExperiences.${index}.businessName`} />
          </Grid2>
          <Grid2 size={4}>
            <FormField
              control={control}
              defaultValue=""
              label="Location (Country)"
              type={FormFieldType.Dropdown}
              options={locationCountryOptions}
              name={`resume.jobExperiences.${index}.locationCountry`}
            />
          </Grid2>
          <Grid2 size={4} />
          <Grid2 size={4}>
            <FormField control={control} label="Title of Position" type={FormFieldType.Text} name={`resume.jobExperiences.${index}.titleOfPosition`} />
          </Grid2>
          <Grid2 size={4}>
            <FormField
              max={0.1} // today
              maxPeriod="days"
              maxDirection="after"
              control={control}
              label="Start Date"
              type={FormFieldType.Date}
              name={`resume.jobExperiences.${index}.dates.startDate`}
            />
          </Grid2>
          <Grid2 size={4}>
            <FormField
              max={0.1} // today
              maxPeriod="days"
              maxDirection="after"
              control={control}
              label="End Date"
              type={FormFieldType.Date}
              name={`resume.jobExperiences.${index}.dates.endDate`}
            />
          </Grid2>
          <Grid2 size={6}>
            <Stack gap={3}>
              <FormField
                control={control}
                label="Average Hours Per Week"
                type={FormFieldType.Text}
                name={`resume.jobExperiences.${index}.averageHoursPerWeek`}
              />
              <FormField
                control={control}
                label="Employment Status"
                type={FormFieldType.Radio}
                options={employmentStatusOptions}
                name={`resume.jobExperiences.${index}.employmentStatus`}
              />
            </Stack>
          </Grid2>
          <Grid2 size={6}>
            <FormField
              multiline
              rows={4}
              control={control}
              label="Description of Duties"
              type={FormFieldType.Text}
              name={`resume.jobExperiences.${index}.descriptionOfDuties`}
            />
          </Grid2>
          <Grid2 size={12}>
            <Stack gap={1} direction="row">
              {index === jobExperiences.length - 1 && (
                <Button startIcon={<AddIcon />} variant="outlined" color="primary" onClick={handleAddJobExperienceBlock}>
                  Add Another Professional Position
                </Button>
              )}
              {jobExperiences.length > 1 && (
                <Button startIcon={<DeleteOutlinedIcon />} variant="outlined" color="error" onClick={() => handleDeleteJobExperienceBlock(index)}>
                  Delete Position
                </Button>
              )}
            </Stack>
          </Grid2>
        </Grid2>
      ))}
      <Divider variant="fullWidth" sx={{ width: '100%', pb: 1, mb: 4 }} />
    </Grid2>
  );
};

const UniversityCollegeExperienceSection = () => {
  const { control } = useFormContext<YourExperienceFormValues>();
  const { fields: uniExperiences, append, remove } = useFieldArray({ control, name: 'resume.uniExperiences' });

  const handleAddUniversityCollegeBlock = useCallback(() => {
    append(uniExperienceDefaultValues);
  }, [append]);

  const handleDeleteUniExperienceBlock = useCallback(
    (i: number) => {
      remove(i);
    },
    [remove],
  );

  return (
    <Grid2 container spacing={3}>
      <Grid2 size={12}>
        <Typography variant="subtitle1">University / College Experience</Typography>
      </Grid2>
      <Grid2 size={12}>
        <Alert variant="outlined" severity="info">
          <Typography variant="body2">Please add all degrees earned since you finished your secondary education</Typography>
        </Alert>
      </Grid2>

      {uniExperiences.map((field, index) => (
        <Grid2 container rowSpacing={2} columnSpacing={4} key={field.id}>
          {index > 0 && <Divider variant="fullWidth" sx={{ width: '100%', mb: 4 }} />}
          <Grid2 size={4}>
            <FormField
              control={control}
              label="Name of University/College"
              type={FormFieldType.Text}
              name={`resume.uniExperiences.${index}.nameOfUniversityCollege`}
            />
          </Grid2>
          <Grid2 size={4}>
            <FormField
              control={control}
              label="Location (Country)"
              type={FormFieldType.Dropdown}
              options={locationCountryOptions}
              name={`resume.uniExperiences.${index}.locationCountry`}
            />
          </Grid2>
          <Grid2 size={4}>
            <FormField control={control} label="Major" type={FormFieldType.Text} name={`resume.uniExperiences.${index}.major`} />
          </Grid2>
          <Grid2 size={6}>
            <FormField control={control} label="Degree" type={FormFieldType.Text} name={`resume.uniExperiences.${index}.degree`} />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              control={control}
              label="Status of Degree"
              type={FormFieldType.Radio}
              options={statusOfDegreeOptions}
              name={`resume.uniExperiences.${index}.statusOfDegree`}
            />
          </Grid2>
          <Grid2 size={12}>
            <Stack direction="row" gap={1}>
              {index === uniExperiences.length - 1 && (
                <Button startIcon={<AddIcon />} variant="outlined" color="primary" onClick={handleAddUniversityCollegeBlock}>
                  Add Another Programme / Course
                </Button>
              )}
              {uniExperiences.length > 1 && (
                <Button startIcon={<DeleteOutlinedIcon />} variant="outlined" color="error" onClick={() => handleDeleteUniExperienceBlock(index)}>
                  Delete Programme / Course
                </Button>
              )}
            </Stack>
          </Grid2>
        </Grid2>
      ))}

      <Divider variant="fullWidth" sx={{ width: '100%', pb: 1, mb: 4 }} />
    </Grid2>
  );
};

const ProfessionalCertificatesSection = () => {
  const { control } = useFormContext<YourExperienceFormValues>();
  const { fields: certificates, append, remove } = useFieldArray({ control, name: 'resume.certificates' });

  const handleAddProfessionalCertificatesBlock = useCallback(() => {
    append(certificateDefaultValues);
  }, [append]);

  const handleDeleteCertificateBlock = useCallback(
    (i: number) => {
      remove(i);
    },
    [remove],
  );

  return (
    <Grid2 container spacing={3}>
      <Grid2 size={12}>
        <Typography variant="subtitle1">Professional Certificates</Typography>
      </Grid2>
      <Grid2 size={12}>
        <Alert sx={{ alignItems: 'flex-start' }} variant="outlined" severity="info">
          <Typography variant="body2">Please add all certificates earned in the past 10 years</Typography>
        </Alert>
      </Grid2>

      {certificates.map((field, index) => (
        <Grid2 container size={12} rowSpacing={2} columnSpacing={4} key={field.id}>
          {index > 0 && <Divider variant="fullWidth" sx={{ width: '100%', mb: 4 }} />}
          <Grid2 container size={6} spacing={2}>
            <Grid2 size={12}>
              <FormField control={control} label="Name of School" type={FormFieldType.Text} name={`resume.certificates.${index}.nameOfSchool`} />
            </Grid2>
            <Grid2 size={12}>
              <FormField control={control} label="Qualification" type={FormFieldType.Text} name={`resume.certificates.${index}.qualification`} />
            </Grid2>
            <Grid2 size={12}>
              <FormField
                max={0.1} // today
                maxPeriod="days"
                maxDirection="after"
                control={control}
                label="Date Earned"
                type={FormFieldType.Date}
                name={`resume.certificates.${index}.dateEarned`}
              />
            </Grid2>
          </Grid2>
          <Grid2 size={6}>
            <FormField
              multiline
              rows={8}
              control={control}
              label="Skill Obtained"
              type={FormFieldType.Text}
              name={`resume.certificates.${index}.skillObtained`}
            />
          </Grid2>
          <Grid2 size={12}>
            <Stack direction="row" gap={1}>
              {index === certificates.length - 1 && (
                <Button startIcon={<AddIcon />} variant="outlined" color="primary" onClick={handleAddProfessionalCertificatesBlock}>
                  Add Another Certificate
                </Button>
              )}
              {certificates.length > 1 && (
                <Button startIcon={<DeleteOutlinedIcon />} variant="outlined" color="error" onClick={() => handleDeleteCertificateBlock(index)}>
                  Delete Certificate
                </Button>
              )}
            </Stack>
          </Grid2>
        </Grid2>
      ))}

      <Divider variant="fullWidth" sx={{ width: '100%', pb: 1, mb: 4 }} />
    </Grid2>
  );
};

const LinkedInProfileSection = () => {
  const { control } = useFormContext<YourExperienceFormValues>();

  return (
    <>
      <Grid2 size={12}>
        <Typography variant="subtitle1">LinkedIn Profile</Typography>
      </Grid2>
      <Grid2 size={4}>
        <FormField control={control} label="LinkedIn Profile URL" type={FormFieldType.Text} name="resume.linkedinUrl" />
      </Grid2>
    </>
  );
};

const GeneralInformationSection = () => {
  const { control } = useFormContext<YourExperienceFormValues>();

  return (
    <Grid2 container rowSpacing={1}>
      <Grid2 size={12}>
        <FormField
          rows={8}
          multiline
          maxLength={MAX_CHARACTERS_DESCRIPTION}
          control={control}
          label="Do you have any international experience (travel, study abroad, work)?"
          type={FormFieldType.Text}
          name="generalInformation.haveInternationalExperience"
        />
      </Grid2>
      <Grid2 size={12}>
        <FormField
          rows={8}
          multiline
          maxLength={MAX_CHARACTERS_DESCRIPTION}
          control={control}
          label="Why do you want to work with exchange students as a Local Coordinator?"
          type={FormFieldType.Text}
          name="generalInformation.wantWorkWithExchangeStudents"
        />
      </Grid2>
      <Grid2 size={12}>
        <FormField
          rows={8}
          multiline
          maxLength={MAX_CHARACTERS_DESCRIPTION}
          control={control}
          label="Do you have experience with teenagers (either personally with your own children and/or other family members, or professionally)?"
          type={FormFieldType.Text}
          name="generalInformation.haveExperienceWithTeenagers"
        />
      </Grid2>
    </Grid2>
  );
};

const MandatoryQuestionsSection = () => {
  const { control, watch } = useFormContext<YourExperienceFormValues>();
  const hadPreviousAffiliationWatch = watch('mandatoryQuestions.hadPreviousAffiliation.value');
  const haveEverHostedStudentWatch = watch('mandatoryQuestions.haveEverHostedStudent.value');
  const contactsCPSWatch = watch('mandatoryQuestions.contactsCPS.value');
  const haveEverBeenConvictedWatch = watch('mandatoryQuestions.haveEverBeenConvicted.value');
  return (
    <Grid2 container rowSpacing={4}>
      <Grid2 size={12}>
        <Alert variant="outlined" severity="info">
          <Typography variant="body2">Questions below are derived from regulatory requirements of J1 visa sponsors.</Typography>
        </Alert>
      </Grid2>
      <Grid2 size={12} container rowSpacing={0}>
        <Grid2 size={12}>
          <FormField
            control={control}
            label="Have you had a previous affiliation in any way with the Secondary School Student program and/or international exchange students as a Local Coordinator (i.e., placing or monitoring exchange students)?"
            type={FormFieldType.Radio}
            options={yesNoOptions}
            name="mandatoryQuestions.hadPreviousAffiliation.value"
          />
        </Grid2>
        <Grid2 size={12}>
          <FormField
            control={control}
            rows={8}
            multiline
            maxLength={MAX_CHARACTERS_DESCRIPTION}
            disabled={shouldDisableField(hadPreviousAffiliationWatch as string, 'yes')}
            label="If yes, please indicate the name of the sponsor that you were affiliated with, and list the dates of your affiliation with that organization."
            type={FormFieldType.Text}
            name="mandatoryQuestions.hadPreviousAffiliation.details"
          />
        </Grid2>
      </Grid2>
      <Grid2 size={12} container rowSpacing={0}>
        <Grid2 size={12}>
          <FormField
            control={control}
            label="Have you or your family ever hosted an exchange student?"
            type={FormFieldType.Radio}
            options={yesNoOptions}
            name="mandatoryQuestions.haveEverHostedStudent.value"
          />
        </Grid2>
        <Grid2 size={12}>
          <FormField
            control={control}
            rows={8}
            multiline
            maxLength={MAX_CHARACTERS_DESCRIPTION}
            disabled={shouldDisableField(haveEverHostedStudentWatch as string, 'yes')}
            label="If yes, how long ago? With which organization?"
            type={FormFieldType.Text}
            name="mandatoryQuestions.haveEverHostedStudent.duration"
          />
        </Grid2>
        <Grid2 size={12}>
          <FormField
            control={control}
            rows={8}
            multiline
            maxLength={MAX_CHARACTERS_DESCRIPTION}
            disabled={shouldDisableField(haveEverHostedStudentWatch as string, 'yes')}
            label="Please provide a brief overview of your hosting experience."
            type={FormFieldType.Text}
            name="mandatoryQuestions.haveEverHostedStudent.details"
          />
        </Grid2>
      </Grid2>

      <Grid2 size={12} container rowSpacing={0}>
        <Grid2 size={12}>
          <FormField
            control={control}
            label="Have you had any contact with any child protective services agency in the past? (U.S. Department of State regulatory question)?"
            type={FormFieldType.Radio}
            options={yesNoOptions}
            name="mandatoryQuestions.contactsCPS.value"
          />
        </Grid2>
        <Grid2 size={12}>
          <FormField
            control={control}
            rows={8}
            multiline
            maxLength={MAX_CHARACTERS_DESCRIPTION}
            disabled={shouldDisableField(contactsCPSWatch as string, 'yes')}
            label="If yes, please indicate when, for what reason(s) and the outcome of your interactions with CPS. If a case involving you or a member of the host family was opened by CSP, is it currently closed? If you are a mandated reporter, please include this information in your explanation."
            type={FormFieldType.Text}
            name="mandatoryQuestions.contactsCPS.explanation"
          />
        </Grid2>
      </Grid2>
      <Grid2 size={12} container rowSpacing={0}>
        <Grid2 size={12}>
          <FormField
            control={control}
            label="Have you ever been convicted of a crime? (Please note that criminal background checks will be conducted in accordance with DOS regulations.)"
            type={FormFieldType.Radio}
            options={yesNoOptions}
            name="mandatoryQuestions.haveEverBeenConvicted.value"
          />
        </Grid2>
        <Grid2 size={12}>
          <FormField
            control={control}
            rows={8}
            multiline
            maxLength={MAX_CHARACTERS_DESCRIPTION}
            disabled={shouldDisableField(haveEverBeenConvictedWatch as string, 'yes')}
            label="If yes, please explain. Provide additional information on the member of the host family involved, when it occurred, what were the circumstances and outcome."
            type={FormFieldType.Text}
            name="mandatoryQuestions.haveEverBeenConvicted.explanation"
          />
        </Grid2>
      </Grid2>
    </Grid2>
  );
};

const PageContainer = ({ children }: PropsWithChildren) => {
  const { setSubmit, setHeaderTitle } = useHeaderContext();
  const { setPageTitle } = usePageTitle();
  const [validationMode, setValidationMode] = useState<ValidationType | ''>('');

  useEffect(() => {
    setHeaderTitle({ title: 'Your Experience', subtitle: 'Resume' });
    setPageTitle('Your Experience');
  }, [setHeaderTitle, setPageTitle]);

  const resolver = useMemo(() => {
    const currentSchema = validationMode === ValidationType.Full ? yourExperienceSchema.required : yourExperienceSchema.optional;
    return zodResolver(currentSchema);
  }, [validationMode]);

  const formMethods = useForm<YourExperienceFormValues>({
    defaultValues,
    resolver,
  });

  const { handleSubmit } = formMethods;

  const onSubmit = useCallback(
    () =>
      handleSubmit((data) => {
        console.log('Form submitted:', data);
      })(),
    [handleSubmit],
  );

  // Submit form when resolver is changed.
  useEffect(() => {
    if (validationMode) {
      onSubmit();
      setValidationMode('');
    }
  }, [resolver, validationMode, onSubmit]);

  useEffect(() => {
    setSubmit(setValidationMode);
  }, [setSubmit, setValidationMode]);

  return (
    <Stack component="form" onSubmit={onSubmit} gap={3}>
      <FormProvider {...formMethods}>
        <Grid2 container spacing={4}>
          {children}
        </Grid2>
      </FormProvider>
    </Stack>
  );
};

type PaperSectionProps = {
  title: string;
  pageTitle?: string;
  children: ReactNode;
};

const PaperSection = ({ title, pageTitle, children }: PaperSectionProps) => {
  return (
    <Paper sx={{ p: 2, width: '100%' }}>
      <Grid2 container spacing={2}>
        {pageTitle && (
          <Grid2 size={12}>
            <Typography variant="subtitle1">{pageTitle}</Typography>
          </Grid2>
        )}
        <Grid2 size={12}>
          <Typography variant="subtitle1">{title}</Typography>
        </Grid2>
        {children}
      </Grid2>
    </Paper>
  );
};
